import { useEffect, useState, useCallback } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { Badge } from "@/components/common/DataTable";
import { knowledgeService, tenantService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Loader2, Pencil, Trash2, Send, CheckCircle2, XCircle, Archive, ChevronDown, ChevronRight,
} from "lucide-react";
import { VISIBILITIES, STATUS_COLOR, VISIBILITY_COLOR } from "./constants";

const EMPTY = { question: "", answer: "", visibility: "Internal", categoryId: "", order: 0, tenantId: "" };

function Field({ label, children }) {
  return <div><label className="block text-sm font-medium text-foreground mb-1">{label}</label>{children}</div>;
}

export default function FaqsPage() {
  const { hasPermission, user } = useAuth();
  const isSuper = user?.isSuperAdmin;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await knowledgeService.listFaqs({ status: statusFilter || undefined, size: 200 });
      setRows(d.items || []);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isSuper) tenantService.list().then((d) => setTenants(d.items || [])).catch(() => {}); }, [isSuper]);
  useEffect(() => {
    const tid = isSuper ? form.tenantId : undefined;
    if (isSuper && !tid) { setCategories([]); return; }
    knowledgeService.listCategories({ tenantId: tid }).then((d) => setCategories(d.items || [])).catch(() => {});
  }, [isSuper, form.tenantId]);

  const canCreate = hasPermission("KNOWLEDGE.CREATE");
  const canEdit = hasPermission("KNOWLEDGE.EDIT");
  const canApprove = hasPermission("KNOWLEDGE.APPROVE");
  const canPublish = hasPermission("KNOWLEDGE.PUBLISH");
  const canDelete = hasPermission("KNOWLEDGE.DELETE");

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...r, categoryId: r.categoryId || "" }); setOpen(true); };

  const save = async () => {
    if (!form.question.trim() || !form.answer.trim()) return toast.error("Question and answer are required");
    if (isSuper && !editing && !form.tenantId) return toast.error("Select a tenant");
    setSaving(true);
    try {
      const payload = { question: form.question, answer: form.answer, visibility: form.visibility,
        categoryId: form.categoryId || null, order: Number(form.order) || 0 };
      if (editing) await knowledgeService.updateFaq(editing.id, payload);
      else { if (isSuper) payload.tenantId = form.tenantId; await knowledgeService.createFaq(payload); }
      toast.success(editing ? "FAQ updated" : "FAQ created");
      setOpen(false); load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = (r) => { if (window.confirm("Delete this FAQ?")) act(() => knowledgeService.removeFaq(r.id), "Deleted"); };

  return (
    <div data-testid="faqs-page">
      <PageHeader title="FAQs" subtitle="Approval-gated, visibility-aware question & answer pairs"
        actions={canCreate && (
          <button onClick={openNew} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90" data-testid="faq-create-btn">
            <Plus className="h-4 w-4" /> New FAQ
          </button>
        )}
      />
      <div className="flex items-center gap-2 mb-4">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="faq-status-filter">
          <option value="">All statuses</option>
          {["Draft", "In Review", "Published", "Archived", "Rejected"].map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div> : (
        <div className="space-y-2" data-testid="faqs-list">
          {rows.length === 0 && <p className="text-sm text-muted-foreground py-10 text-center">No FAQs yet</p>}
          {rows.map((r) => {
            const isOpen = expanded[r.id];
            return (
              <div key={r.id} className="bg-card rounded-xl border border-border" data-testid={`faq-row-${r.id}`}>
                <div className="flex items-start justify-between gap-3 p-4">
                  <button onClick={() => setExpanded((p) => ({ ...p, [r.id]: !p[r.id] }))} className="flex items-start gap-2 text-left flex-1 min-w-0" data-testid={`faq-toggle-${r.id}`}>
                    {isOpen ? <ChevronDown className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" /> : <ChevronRight className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />}
                    <div className="min-w-0">
                      <p className="font-medium text-foreground">{r.question}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge color={STATUS_COLOR[r.status]}>{r.status}</Badge>
                        <Badge color={VISIBILITY_COLOR[r.visibility]}>{r.visibility}</Badge>
                      </div>
                    </div>
                  </button>
                  <div className="flex items-center gap-1 shrink-0">
                    {canEdit && ["Draft", "Rejected"].includes(r.status) && (
                      <button onClick={() => act(() => knowledgeService.faqAction(r.id, "submit"), "Submitted")} disabled={busy} className="p-1.5 rounded-md hover:bg-muted text-primary" title="Submit for review" data-testid={`faq-submit-${r.id}`}><Send className="h-4 w-4" /></button>
                    )}
                    {canApprove && r.status === "In Review" && (
                      <>
                        <button onClick={() => act(() => knowledgeService.faqAction(r.id, "approve"), "Approved")} disabled={busy} className="p-1.5 rounded-md hover:bg-emerald-500/10 text-emerald-600" title="Approve & publish" data-testid={`faq-approve-${r.id}`}><CheckCircle2 className="h-4 w-4" /></button>
                        <button onClick={() => { const x = window.prompt("Rejection reason?"); if (x !== null) act(() => knowledgeService.faqAction(r.id, "reject", { reason: x }), "Rejected"); }} disabled={busy} className="p-1.5 rounded-md hover:bg-red-500/10 text-red-600" title="Reject" data-testid={`faq-reject-${r.id}`}><XCircle className="h-4 w-4" /></button>
                      </>
                    )}
                    {canPublish && r.status === "Published" && (
                      <button onClick={() => act(() => knowledgeService.faqAction(r.id, "archive"), "Archived")} disabled={busy} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground" title="Archive" data-testid={`faq-archive-${r.id}`}><Archive className="h-4 w-4" /></button>
                    )}
                    {canEdit && <button onClick={() => openEdit(r)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground" title="Edit" data-testid={`faq-edit-${r.id}`}><Pencil className="h-4 w-4" /></button>}
                    {canDelete && <button onClick={() => remove(r)} className="p-1.5 rounded-md hover:bg-destructive/10 text-destructive" title="Delete" data-testid={`faq-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>}
                  </div>
                </div>
                {isOpen && <div className="px-4 pb-4 pl-10 text-sm text-muted-foreground border-t border-border pt-3" data-testid={`faq-answer-${r.id}`}>{r.answer}</div>}
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-card text-foreground rounded-2xl w-full max-w-lg p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="faq-dialog">
            <h3 className="text-lg font-bold mb-4">{editing ? "Edit FAQ" : "New FAQ"}</h3>
            <div className="space-y-3.5">
              {isSuper && !editing && (
                <Field label="Tenant *">
                  <select value={form.tenantId} onChange={(e) => setForm({ ...form, tenantId: e.target.value, categoryId: "" })} className="ciq-input" data-testid="faq-tenant">
                    <option value="">Select tenant…</option>
                    {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName || t.name || t.id}</option>)}
                  </select>
                </Field>
              )}
              <Field label="Question *"><input value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} className="ciq-input" data-testid="faq-question" /></Field>
              <Field label="Answer *"><textarea value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} className="ciq-input" rows={4} data-testid="faq-answer-input" /></Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Visibility"><select value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })} className="ciq-input" data-testid="faq-visibility">{VISIBILITIES.map((v) => <option key={v}>{v}</option>)}</select></Field>
                <Field label="Category"><select value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })} className="ciq-input" data-testid="faq-category"><option value="">None</option>{categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
                <Field label="Order"><input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="ciq-input" data-testid="faq-order" /></Field>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted">Cancel</button>
              <button onClick={save} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60" data-testid="faq-save">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Save" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
